import { useEffect, useMemo, useCallback, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { useAppDispatch } from "@store/hook";
import { toggleDREItem, useExpandedDREItems } from "@store/dashboard/dashboardSlice";
import dashboardService from "@services/dashboard";
import {
  OnboardingModal,
  MonthNavigation,
  KPICards,
  CategoryCard,
  DRETable,
  FinancialEvolutionChart,
  DraggablePanel,
} from "@components/Dashboard";
import { Button } from "@rio-cloud/rio-uikit";
import { useDashboardOnboarding } from "@hooks/useDashboardOnboarding";
import PanelShell from "@components/Dashboard/PanelShell";
import { getMonthDateRange, normalizeLast12MonthsData } from "@utils/dateRange";
import type {
  DashboardFilter,
  KPIData,
  ExpenseCategory,
  RevenueCategory,
  DREHierarchicalResponse,
  DREHierarchicalLine,
  DREHierarchicalNode,
  FinancialEvolution,
} from "../../types/dashboard";
import type { ExpandNodeParams } from "@components/Dashboard/DRETable";
import { setEndDate, setStartDate, useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import {
  useGetStoredSelectedBranches,
  useGetStoredSelectedSegments,
  useGetStoredSelectedClients,
  useGetStoredSelectedVehicles,
  useGetStoredSelectedDrivers,
  clearAllFilters,
} from "@store/tmsAssetTree/tmsAssetTreeSlice";
import SelectedFilters from "@components/SelectedFilters";

type PanelType = "expenses" | "revenue" | "dre" | "chart";
type DragPosition = "left" | "right" | "top" | "bottom";

interface Panel {
  id: PanelType;
  order: number;
}

const DEFAULT_PANELS: Panel[] = [
  { id: "dre", order: 0 },
  { id: "expenses", order: 1 },
  { id: "revenue", order: 2 },
  { id: "chart", order: 3 },
];

const DEFAULT_PANEL_PAIRS: string[] = ["expenses|revenue"];

export default function DashboardScreen() {
  const dispatch = useAppDispatch();
  const expandedDREItems = useExpandedDREItems();
  const navigate = useNavigate();

  const {
    showOnboarding,
    onboardingStep,
    onboarding,
    handleCloseOnboarding,
    handleNextStep,
    handlePrevStep,
    handleFilialNameChange,
    handleNumFiliaisChange,
    onboardingLoading,
    onboardingSaving,
  } = useDashboardOnboarding();

  // Filtros do sidebar (Redux)
  const selectedBranches = useGetStoredSelectedBranches();
  const selectedSegments = useGetStoredSelectedSegments();
  const selectedClients = useGetStoredSelectedClients();
  const selectedVehicles = useGetStoredSelectedVehicles();
  const selectedDrivers = useGetStoredSelectedDrivers();

  const startDate = useGetStoredStartDate();
  const endDate = useGetStoredEndDate();

  const [panels, setPanels] = useState<Panel[]>(DEFAULT_PANELS);
  const [panelPairs, setPanelPairs] = useState<string[]>(DEFAULT_PANEL_PAIRS);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => new Date());
  const [kpi, setKpi] = useState<KPIData | null>(null);
  const [kpiLoading, setKpiLoading] = useState(true);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[] | null>(null);
  const [revenueCategories, setRevenueCategories] = useState<RevenueCategory[] | null>(null);
  const [dre, setDre] = useState<DREHierarchicalResponse | null>(null);
  const [dreLoading, setDreLoading] = useState(true);
  const [dreLoadingNodes, setDreLoadingNodes] = useState<string[]>([]);
  const [financialEvolution, setFinancialEvolution] = useState<FinancialEvolution[] | null>(null);
  const [financialEvolutionLoading, setFinancialEvolutionLoading] = useState(true);

  const isDateFilterIncomplete = Boolean((startDate && !endDate) || (!startDate && endDate));
  const monthRange = useMemo(() => getMonthDateRange(selectedMonth), [selectedMonth]);
  const requestFilters = useMemo<DashboardFilter>(() => {
    const useCustomRange = Boolean(startDate && endDate);
    const payload: DashboardFilter = {
      startDate: useCustomRange ? startDate : monthRange.startDate,
      endDate: useCustomRange ? endDate : monthRange.endDate,
    };

    // Filtros do sidebar (Redux)
    if (selectedBranches?.length) payload.branchIds = selectedBranches;
    if (selectedSegments?.length) payload.segmentIds = selectedSegments;
    if (selectedClients?.length) payload.clientIds = selectedClients;
    if (selectedVehicles?.length) payload.vehicleIds = selectedVehicles;
    if (selectedDrivers?.length) payload.driverIds = selectedDrivers;

    return payload;
  }, [
    monthRange,
    startDate,
    endDate,
    selectedBranches,
    selectedSegments,
    selectedClients,
    selectedVehicles,
    selectedDrivers,
  ]);

  const fetchKPI = useCallback(async () => {
    if (isDateFilterIncomplete) return;
    setKpiLoading(true);
    try {
      const data = await dashboardService.getKPI(requestFilters);
      setKpi(data);
    } catch (error) {
      console.error("Error fetching KPI:", error);
    } finally {
      setKpiLoading(false);
    }
  }, [requestFilters, isDateFilterIncomplete]);

  const fetchExpenseCategories = useCallback(async () => {
    if (isDateFilterIncomplete) return;
    try {
      const data = await dashboardService.getExpenseCategories(requestFilters);
      setExpenseCategories(data);
    } catch (error) {
      console.error("Error fetching expense categories:", error);
    }
  }, [requestFilters, isDateFilterIncomplete]);

  const fetchRevenueCategories = useCallback(async () => {
    if (isDateFilterIncomplete) return;
    try {
      const data = await dashboardService.getRevenueCategories(requestFilters);
      setRevenueCategories(data);
    } catch (error) {
      console.error("Error fetching revenue categories:", error);
    }
  }, [requestFilters, isDateFilterIncomplete]);

  const fetchDRE = useCallback(async () => {
    if (isDateFilterIncomplete) return;
    setDreLoading(true);
    try {
      const data = await dashboardService.getDRE(requestFilters);
      setDre(data);
    } catch (error) {
      console.error("Error fetching DRE data:", error);
    } finally {
      setDreLoading(false);
    }
  }, [requestFilters, isDateFilterIncomplete]);

  const fetchFinancialEvolution = useCallback(async () => {
    setFinancialEvolutionLoading(true);
    try {
      const filtersWithoutDate: DashboardFilter = {
        branchIds: selectedBranches?.length ? selectedBranches : undefined,
        segmentIds: selectedSegments?.length ? selectedSegments : undefined,
        clientIds: selectedClients?.length ? selectedClients : undefined,
        vehicleIds: selectedVehicles?.length ? selectedVehicles : undefined,
        driverIds: selectedDrivers?.length ? selectedDrivers : undefined,
      };
      const data = await dashboardService.getFinancialEvolutionLast12Months(filtersWithoutDate);
      const normalizedData = normalizeLast12MonthsData(data, { expenses: 0, revenue: 0, profit: 0, profitMargin: 0 });
      setFinancialEvolution(normalizedData);
    } catch (error) {
      console.error("Error fetching financial evolution data:", error);
    } finally {
      setFinancialEvolutionLoading(false);
    }
  }, [selectedBranches, selectedSegments, selectedClients, selectedVehicles, selectedDrivers]);

  useEffect(() => {
    fetchKPI();
  }, [fetchKPI]);

  useEffect(() => {
    fetchExpenseCategories();
  }, [fetchExpenseCategories]);

  useEffect(() => {
    fetchRevenueCategories();
  }, [fetchRevenueCategories]);

  useEffect(() => {
    fetchDRE();
  }, [fetchDRE]);

  useEffect(() => {
    fetchFinancialEvolution();
  }, [fetchFinancialEvolution]);

  const currentMonth = useMemo(
    () => selectedMonth.toLocaleDateString("pt-BR", { month: "long", year: "numeric" }).toUpperCase(),
    [selectedMonth]
  );

  const goToPrevMonth = useCallback(() => {
    if (startDate && endDate) {
      const currentStart = moment(startDate, "YYYY-MM-DD");
      const newStart = currentStart.clone().subtract(1, "month").startOf("month");
      const newEnd = newStart.clone().endOf("month");

      dispatch(setStartDate(newStart.format("YYYY-MM-DD")));
      dispatch(setEndDate(newEnd.format("YYYY-MM-DD")));
    } else {
      setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  }, [startDate, endDate, dispatch]);

  const goToNextMonth = useCallback(() => {
    if (startDate && endDate) {
      // Se houver datas customizadas, navega mês a mês baseado na data inicial
      const currentStart = moment(startDate, "YYYY-MM-DD");
      const newStart = currentStart.clone().add(1, "month").startOf("month");
      const newEnd = newStart.clone().endOf("month");

      dispatch(setStartDate(newStart.format("YYYY-MM-DD")));
      dispatch(setEndDate(newEnd.format("YYYY-MM-DD")));
    } else {
      setSelectedMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }
  }, [startDate, endDate, dispatch]);

  const toggleDRE = (id: string) => {
    dispatch(toggleDREItem(id));
  };

  const updateNodeInChildren = useCallback(
    (nodes: DREHierarchicalNode[], nodeId: string, newChildren: DREHierarchicalNode[]): DREHierarchicalNode[] => {
      return nodes.map((node) => {
        if (node.id === nodeId) {
          return { ...node, children: newChildren };
        }
        if (node.children && node.children.length > 0) {
          const updatedChildren = updateNodeInChildren(node.children, nodeId, newChildren);
          const wasUpdated = updatedChildren.some((child, idx) => child !== node.children![idx]);
          if (wasUpdated) {
            return { ...node, children: updatedChildren };
          }
        }
        return node;
      });
    },
    []
  );

  const updateNodeChildren = useCallback(
    (lines: DREHierarchicalLine[], nodeId: string, children: DREHierarchicalNode[]): DREHierarchicalLine[] => {
      const newLines = lines.map((line) => {
        if (line.children) {
          const updatedChildren = updateNodeInChildren(line.children, nodeId, children);
          const wasUpdated = updatedChildren.some((child, idx) => child !== line.children![idx]);
          if (wasUpdated) {
            return { ...line, children: updatedChildren };
          }
        }
        return line;
      });
      return newLines;
    },
    [updateNodeInChildren]
  );

  const handleExpandDRENode = useCallback(
    async (params: ExpandNodeParams) => {
      const { nodeId, compositeId, nodeType, lineType, parentPath } = params;

      setDreLoadingNodes((prev) => [...prev, compositeId]);

      try {
        const response = await dashboardService.expandDRENode(requestFilters, {
          nodeId,
          nodeType,
          lineType,
          parentPath,
        });

        if (response.children && response.children.length > 0) {
          setDre((prevDre) => {
            if (!prevDre) {
              return prevDre;
            }
            const newLines = updateNodeChildren(prevDre.lines, compositeId, response.children);
            return {
              ...prevDre,
              lines: newLines,
            };
          });
        }
      } catch (error) {
        console.error("Error expanding DRE node:", error);
      } finally {
        setDreLoadingNodes((prev) => prev.filter((id) => id !== compositeId));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [requestFilters, updateNodeChildren]
  );

  const handleExportDRE = useCallback(
    async (view: "general" | "branch" | "segment" | "vehicle" | "driver") => {
      try {
        await dashboardService.exportDRE(requestFilters, view);
      } catch (error) {
        console.error("Error exporting DRE:", error);
      }
    },
    [requestFilters]
  );

  const movePanelTo = useCallback(
    (dragIndex: number, hoverIndex: number, position: DragPosition, dragId: string, hoverId: string) => {
      const insertBefore = position === "left" || position === "top";
      let targetIndex = insertBefore ? hoverIndex : hoverIndex + 1;
      if (dragIndex < targetIndex) {
        targetIndex -= 1;
      }

      setPanels((prevPanels) => {
        const newPanels = [...prevPanels];
        const [draggedPanel] = newPanels.splice(dragIndex, 1);
        newPanels.splice(targetIndex, 0, draggedPanel);
        const normalizedPanels = newPanels.map((panel, index) => ({ ...panel, order: index }));

        setPanelPairs((prevPairs) => {
          const orderIds = normalizedPanels.map((panel) => panel.id);
          let nextPairs = new Set(prevPairs);

          const removePairsFor = (panelId: PanelType, pairs: Set<string>) => {
            return new Set(
              Array.from(pairs).filter((pair) => !pair.startsWith(`${panelId}|`) && !pair.endsWith(`|${panelId}`))
            );
          };

          const normalizePairs = (pairs: Set<string>) => {
            const validPairs = new Set<string>();
            for (let i = 0; i < orderIds.length - 1; i += 1) {
              const pairKey = `${orderIds[i]}|${orderIds[i + 1]}`;
              if (pairs.has(pairKey)) {
                validPairs.add(pairKey);
              }
            }
            return validPairs;
          };

          const dragPanelId = dragId as PanelType;
          const hoverPanelId = hoverId as PanelType;

          if (position === "left") {
            nextPairs = removePairsFor(dragPanelId, nextPairs);
            nextPairs = removePairsFor(hoverPanelId, nextPairs);
            nextPairs.add(`${dragPanelId}|${hoverPanelId}`);
          }

          if (position === "right") {
            nextPairs = removePairsFor(dragPanelId, nextPairs);
            nextPairs = removePairsFor(hoverPanelId, nextPairs);
            nextPairs.add(`${hoverPanelId}|${dragPanelId}`);
          }

          if (position === "top" || position === "bottom") {
            nextPairs = removePairsFor(dragPanelId, nextPairs);
          }

          nextPairs = normalizePairs(nextPairs);
          return Array.from(nextPairs);
        });

        return normalizedPanels;
      });

      return targetIndex;
    },
    []
  );

  const handleClearFilters = useCallback(() => {
    const now = moment();
    dispatch(setStartDate(now.startOf("month").format("YYYY-MM-DD")));
    dispatch(setEndDate(now.endOf("month").format("YYYY-MM-DD")));
    setShowDateRangePicker(false);
    setSelectedMonth(new Date());
    dispatch(clearAllFilters());
  }, [dispatch]);

  const hasFilters = Boolean(
    selectedBranches?.length ||
    selectedSegments?.length ||
    selectedClients?.length ||
    selectedVehicles?.length ||
    selectedDrivers?.length ||
    startDate ||
    endDate
  );

  const buildPeriodQuery = (extraParams: Record<string, string | undefined> = {}) => {
    const params = new URLSearchParams();
    if (requestFilters.startDate) params.set("startDate", requestFilters.startDate);
    if (requestFilters.endDate) params.set("endDate", requestFilters.endDate);
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
    return `?${params.toString()}`;
  };

  const renderPanel = (panelId: PanelType) => {
    switch (panelId) {
      case "expenses":
        return (
          <CategoryCard
            addButtonText="Nova despesa"
            title="Top Categorias de Despesas"
            categories={expenseCategories ?? []}
            onAddClick={() => navigate(`/tms/expenses${buildPeriodQuery({ add: "expense" })}`)}
            onViewClick={() => navigate(`/tms/expenses${buildPeriodQuery()}`)}
            emptyStateText="Nenhuma despesa cadastrada no período"
          />
        );
      case "revenue":
        return (
          <CategoryCard
            addButtonText="Nova receita"
            title="Top Categorias de Receitas"
            categories={revenueCategories ?? []}
            onAddClick={() => navigate(`/tms/recipes${buildPeriodQuery({ add: "receipt" })}`)}
            onViewClick={() => navigate(`/tms/recipes${buildPeriodQuery()}`)}
          />
        );
      case "dre":
        return (
          <PanelShell loading={dreLoading} loadingLabel="DRE" hasData={Boolean(dre?.lines?.length)}>
            <DRETable
              data={dre?.lines ?? []}
              expandedItems={expandedDREItems}
              loadingNodes={dreLoadingNodes}
              onToggle={toggleDRE}
              onExpandNode={handleExpandDRENode}
              onExportDRE={handleExportDRE}
            />
          </PanelShell>
        );
      case "chart":
        return (
          <PanelShell
            loading={financialEvolutionLoading}
            loadingLabel="Evolução financeira"
            hasData={Boolean(financialEvolution && financialEvolution.length)}
          >
            <FinancialEvolutionChart data={financialEvolution ?? []} />
          </PanelShell>
        );
      default:
        return null;
    }
  };

  if (onboardingLoading) {
    return (
      <div className="width-100pct height-100pct display-flex justify-content-center align-items-center bg-lighest padding-25">
        <PanelShell loading loadingLabel="" hasData={false}>
          <></>
        </PanelShell>
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="width-100pct height-100pct display-flex flex-column bg-lighest">
        <OnboardingModal
          show={showOnboarding}
          step={onboardingStep}
          onNext={handleNextStep}
          onPrev={handlePrevStep}
          isSaving={onboardingSaving}
          onClose={handleCloseOnboarding}
          numFiliais={onboarding.numFiliais}
          filiaisNames={onboarding.filiaisNames}
          onNumFiliaisChange={handleNumFiliaisChange}
          onFilialNameChange={handleFilialNameChange}
        />

        <div className="flex-1 overflow-y-auto">
          <div className="display-flex flex-column gap-20 padding-25 bg-light">
            <MonthNavigation
              endDate={endDate}
              startDate={startDate}
              currentMonth={currentMonth}
              onPrevMonth={goToPrevMonth}
              onNextMonth={goToNextMonth}
              onEndDateChange={setEndDate}
              onStartDateChange={setStartDate}
              showDateRangePicker={showDateRangePicker}
              onClick={() => setShowDateRangePicker(!showDateRangePicker)}
              onCloseDateRangePicker={() => setShowDateRangePicker(false)}
            />

            <SelectedFilters requestFilters={requestFilters} />
          </div>

          <div className="padding-25">
            <PanelShell loading={kpiLoading} loadingLabel="KPIs" hasData={Boolean(kpi)} className="margin-bottom-20">
              {kpi && <KPICards kpi={kpi} />}
            </PanelShell>

            <>
              {(() => {
                const sortedPanels = [...panels].sort((a, b) => a.order - b.order);
                const rows: React.ReactElement[] = [];
                const pairSet = new Set(panelPairs);
                let i = 0;

                while (i < sortedPanels.length) {
                  const currentPanel = sortedPanels[i];
                  const nextPanel = sortedPanels[i + 1];
                  const shouldPair = Boolean(nextPanel) && pairSet.has(`${currentPanel.id}|${nextPanel.id}`);

                  if (shouldPair && nextPanel) {
                    rows.push(
                      <div key={`row-${i}`} className="display-grid grid-cols-2 gap-20 margin-bottom-20">
                        <DraggablePanel id={currentPanel.id} index={i} movePanelTo={movePanelTo}>
                          {renderPanel(currentPanel.id)}
                        </DraggablePanel>
                        <DraggablePanel id={nextPanel.id} index={i + 1} movePanelTo={movePanelTo}>
                          {renderPanel(nextPanel.id)}
                        </DraggablePanel>
                      </div>
                    );
                    i += 2;
                    continue;
                  }

                  rows.push(
                    <div key={`row-${i}`} className="margin-bottom-20">
                      <DraggablePanel id={currentPanel.id} index={i} movePanelTo={movePanelTo}>
                        {renderPanel(currentPanel.id)}
                      </DraggablePanel>
                    </div>
                  );
                  i += 1;
                }

                return rows;
              })()}
            </>
          </div>
        </div>
      </div>
    </DndProvider>
  );
}
